import { Editor, Transforms, Element, Range, Text, node } from "slate";

const SlateEditorTools = {
  isBoldMarkActive(editor: any) {
    const marks: any = Editor.marks(editor);
    return marks ? marks.bold === true : false;
  },

  isItalicMarkActive(editor: any) {
    const marks: any = Editor.marks(editor);
    return marks ? marks.italic === true : false;
  },

  toggleBoldMark(editor: any) {
    const isActive = SlateEditorTools.isBoldMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "bold");
    } else {
      Editor.addMark(editor, "bold", true);
    }
  },

  toggleItalicMark(editor: any) {
    const isActive = SlateEditorTools.isItalicMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "italic");
    } else {
      Editor.addMark(editor, "italic", true);
    }
  },

  isHeading1Active(editor:any){
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === 'h1',
    })
    return !!match
    
  },

  // Heading functionality
  toggleHeading1(editor: any) {
    Transforms.setNodes<any>(
      editor,
      { type: SlateEditorTools.isHeading1Active(editor) ? 'paragraph' : 'h1' },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n)}
    )
  },

  isHeading2Active(editor:any){
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === 'h2',
    })
    return !!match
  },

  toggleHeading2(editor: any) {
    const isActive = SlateEditorTools.isHeading2Active(editor)
    Transforms.setNodes<any>(
      editor,
      { type: isActive ? 'paragraph' : 'h2' },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n)}
    )
  },

  isUnderlineMarkActive(editor: any) {
    const marks = Editor.marks(editor) as any;
    return marks ? marks.underline === true : false;
  },

  toggleUnderlineMark(editor: any) {
    const isActive = SlateEditorTools.isUnderlineMarkActive(editor);
    if (isActive) {
      Editor.removeMark(editor, "underline");
    } else {
      Editor.addMark(editor, "underline", true);
    }
  },

  isCodeActive(editor: any){
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === 'code',
    })
    return !!match
  },

  // Element
  toggleCodeBlock(editor: any) {
    const isActive = SlateEditorTools.isCodeActive(editor)
    Transforms.setNodes<any>(
      editor,
      { type: isActive ? 'paragraph' : 'code' },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n), }
    )
  },

  isBulletActive(editor:any){
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === 'bullet',
    })
    return !!match
  },

  toggleBulletBlock(editor: any) {
    const isActive = SlateEditorTools.isBulletActive(editor) 
    Transforms.setNodes<any>(
      editor,
      { type: isActive ? 'paragraph' : 'bullet' },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n) }
    )
  },

  isNumberBlock(editor:any) {
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === 'orderedList',
    })
    return !!match
  },

  toggleNumberBlock(editor: any) {
    const isActive = SlateEditorTools.isNumberBlock(editor)
    Transforms.setNodes<any>(
      editor,
      { type: isActive ? 'paragraph' : 'orderedList' },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n) }
    )
  },

  isLeftActive(editor:any) {
    const [match]: any = Editor.nodes(editor, {
      match: (n: any) =>  n.align === 'left',
    });
    return !!match;
  },

  isCenterActive(editor:any) {
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.align === 'center'
    })
    return !!match
  },

  isRightActive(editor:any) {
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.align === 'right'
    })
    return !!match
  },

  isJustifyActive(editor:any) {
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.align === 'justify'
    })
    return !!match
  },

  toggleAlignment(editor: any, alignment: 'left' | 'center' | 'right' | 'justify') {
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.type === alignment
    })
    
    Transforms.setNodes<any>(
      editor,
      { align: alignment },
      { match: n => Element.isElement(n) && Editor.isBlock(editor, n) }
    )
  },

  toggleFontSize(editor:any, size:any){
    const isActive = SlateEditorTools.isFontSizeActive(editor, size);
    Transforms.setNodes<any>(
      editor,
      { fontSize: isActive ? null : size },
      { match: n => Text.isText(n), split: true }
    );
  },
  
  isFontSizeActive(editor:any, size:any){
    const [match]:any = Editor.nodes(editor, {
      match: (n:any) => n.fontSize === size,
      universal: true,
    });
    return !!match;
  },
  

  toggleImageUpload(editor: any, imageUrl?:string) {
    const [match]:any = Editor.nodes(editor, {
      match: (n: any) => n.type === 'image',
    });
  
    if (match) {

      Transforms.setNodes<any>(editor, { type: 'paragraph' });
    } else {

      const imageNode = {
        type: 'image',
        url: imageUrl,
        children: [{ text: '' }], 
      };
      Transforms.insertNodes(editor, imageNode);
      
      const paragraph = {
        type: 'paragraph',
        children: [{ text: '' }],
      }

      Transforms.insertNodes(editor, paragraph);
    }},


    isLinkActive(editor: any) {
      const [link]:any = Editor.nodes(editor, { match: (n: any) => n.type === "link" });
      return !!link;
    },
  
    toggleLink(editor: any, url: string) {
      const isActive = SlateEditorTools.isLinkActive(editor);
  
      if (isActive) {
        Transforms.unwrapNodes(editor, { match: (n: any) => n.type === "link" });
      } else if (url) {
        if (editor.selection && Range.isCollapsed(editor.selection)) {

          Transforms.insertText(editor, url);
        } else {
          const linkNode = {
            type: "link",
            url,
            children: [{ text: Editor.string(editor, editor.selection) }],
          };

          Transforms.wrapNodes(editor, linkNode, { split: true });

          Transforms.collapse(editor, { edge: "end" });
        }
      }
    },
  

    getLinkUrl(editor: any) {
      const [link]:any = Editor.nodes(editor, { match: (n: any) => n.type === 'link' });
      return link ? link[0].url : undefined;
    },

    toggleTextColor(editor:any, color:any){
      const isActive = SlateEditorTools.isTextColorActive(editor, color);
      Transforms.setNodes<any>(
        editor,
        { color: isActive ? null : color },
        { match: n => Text.isText(n), split: true }
      );
    },
    
    isTextColorActive(editor:any, color:any){
      const [match]:any = Editor.nodes(editor, {
        match: (n:any) => n.color === color,
        universal: true,
      });
      return !!match;
    },
    
    toggleBgColor(editor:any, color:any){
      const isActive = SlateEditorTools.isBgColorActive(editor, color);
      Transforms.setNodes<any>(
        editor,
        { backgroundColor: isActive ? null : color },
        { match: n => Text.isText(n), split: true }
      );
    },
    
    isBgColorActive(editor:any, color:any){
      const [match]:any = Editor.nodes(editor, {
        match: (n:any) => n.backgroundColor === color,
        universal: true,
      });
      return !!match;
    }
};

export default SlateEditorTools;
